import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, Users, ArrowRight, Key, Copy, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { adminLogin, memberLogin, fetchMe } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Personal code
  const [personalCode, setPersonalCode] = useState("");
  const [showPersonal, setShowPersonal] = useState(false);

  // Daily code
  const [dailyCode, setDailyCode] = useState("");
  const [step, setStep] = useState("login"); // login, daily_name, welcome
  const [name, setName] = useState("");
  const [welcomeData, setWelcomeData] = useState(null);

  // ── Login with personal/admin code ──
  const handlePersonalLogin = async (e) => {
    e.preventDefault();
    const code = personalCode.trim();
    if (!code) return;
    setLoading(true);

    try {
      // Try admin login first (silently)
      try {
        const result = await adminLogin(code);
        if (result?.success) {
          toast.success("Velkommen!");
          window.location.href = "/medlemmer";
          return;
        }
      } catch {
        // Not an admin code, try member login
      }
      // Try member login
      await memberLogin(code);
      toast.success("Velkommen tilbage!");
      window.location.href = "/medlemmer";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ugyldig kode. Har du ikke en kode? Bed en administrator om en dagskode.");
    } finally {
      setLoading(false);
    }
  };

  // ── Submit daily code ──
  const handleDailySubmit = async (e) => {
    e.preventDefault();
    const code = dailyCode.trim().toUpperCase();
    if (!code) return;
    setLoading(true);

    try {
      const res = await axios.post(`${API}/member-codes/smart-login`, { code });
      if (res.data.type === "daily") {
        setStep("daily_name");
      } else {
        toast.error("Dette er ikke en dagskode. Brug feltet 'Din kode' ovenfor i stedet.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ugyldig eller udløbet dagskode");
    } finally {
      setLoading(false);
    }
  };

  // ── Register with daily code ──
  const handleDailyRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);

    try {
      const res = await axios.post(`${API}/member-codes/daily-register`, {
        code: dailyCode.trim().toUpperCase(),
        name: name.trim(),
      });

      localStorage.setItem("mc_token", res.data.token);
      await fetchMe();
      setWelcomeData(res.data);
      setStep("welcome");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke oprette profil");
    } finally {
      setLoading(false);
    }
  };

  // ── Welcome screen ──
  if (step === "welcome" && welcomeData) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-12">
        <div className="w-full max-w-md">
          <div className="mc-card p-8 text-center border-green-500/30" data-testid="welcome-screen">
            <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl font-['Oswald'] font-bold text-white mb-2">VELKOMMEN!</h1>
            <p className="text-gray-400 mb-6">{welcomeData.message}</p>

            <div className="bg-black/50 rounded-xl p-6 border border-green-500/30 mb-4">
              <p className="text-gray-400 text-sm mb-2">Din personlige kode:</p>
              <p className="text-3xl font-mono font-bold text-green-400 tracking-widest" data-testid="personal-code">
                {welcomeData.personal_code}
              </p>
            </div>

            <button
              onClick={() => { navigator.clipboard.writeText(welcomeData.personal_code); toast.success("Kode kopieret!"); }}
              className="w-full mb-3 py-3 rounded-xl font-['Oswald'] uppercase tracking-wider bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30 transition-colors flex items-center justify-center gap-2"
              data-testid="copy-personal-code-btn"
            >
              <Copy className="w-4 h-4" /> Kopier kode
            </button>

            <p className="text-yellow-400 text-sm mb-4">
              GEM DENNE KODE! Brug den til at logge ind fremover under "Din kode".
            </p>

            <p className="text-gray-500 text-xs mb-6">
              Du kan også oprette din egen personlige kode under "Min Kode" i Medlemsområdet.
            </p>

            <button
              onClick={() => navigate("/medlemmer")}
              className="w-full chrome-btn py-3 rounded-xl font-['Oswald'] uppercase tracking-wider"
              data-testid="go-to-members-btn"
            >
              Gå til Medlemsområde
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Daily code: enter name ──
  if (step === "daily_name") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-12">
        <div className="w-full max-w-md">
          <div className="mc-card p-8" data-testid="daily-name-form">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-2">BLIV MEDLEM</h2>
              <p className="text-gray-400 text-sm">Dagskode godkendt! Hvad hedder du?</p>
            </div>
            <form onSubmit={handleDailyRegister} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Dit navn</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mc-input w-full text-center text-lg"
                  placeholder="Indtast dit navn"
                  autoFocus
                  data-testid="daily-name-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading || name.trim().length < 2}
                className="w-full py-3 rounded-xl font-['Oswald'] uppercase tracking-wider bg-green-600 hover:bg-green-500 text-white disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                data-testid="daily-register-submit"
              >
                {loading ? "Opretter..." : <>Opret min adgang <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button
                type="button"
                onClick={() => { setStep("login"); setDailyCode(""); setName(""); }}
                className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 transition-colors"
              >
                ← Tilbage
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Main login page with two fields ──
  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-12">
      <div className="w-full max-w-md space-y-6" data-testid="login-page">
        <div className="text-center mb-4">
          <h1 className="text-4xl font-['Oswald'] font-bold chrome-text mb-2">LOG IND</h1>
          <p className="text-gray-400">Indtast din kode for at få adgang</p>
        </div>

        {/* ── FIELD 1: Din kode (personal/admin code) ── */}
        <form onSubmit={handlePersonalLogin} className="mc-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#00d4ff]/10 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-[#00d4ff]" />
            </div>
            <div>
              <h2 className="text-lg font-['Oswald'] font-bold text-white">DIN KODE</h2>
              <p className="text-gray-500 text-xs">Din personlige adgangskode</p>
            </div>
          </div>

          <div className="relative mb-4">
            <input
              type={showPersonal ? "text" : "password"}
              value={personalCode}
              onChange={(e) => setPersonalCode(e.target.value)}
              className="mc-input w-full pr-12 text-center text-lg tracking-widest font-mono py-3"
              placeholder="Indtast din kode"
              autoComplete="off"
              autoFocus
              data-testid="personal-code-input"
            />
            <button
              type="button"
              onClick={() => setShowPersonal(!showPersonal)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
            >
              {showPersonal ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading || !personalCode.trim()}
            className="w-full py-3 rounded-xl font-['Oswald'] uppercase tracking-wider bg-gradient-to-r from-[#00d4ff] to-[#0090ff] text-black hover:shadow-lg hover:shadow-[#00d4ff]/20 disabled:opacity-50 disabled:hover:shadow-none transition-all flex items-center justify-center gap-2"
            data-testid="personal-login-submit"
          >
            {loading ? "Logger ind..." : <>Log ind <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        {/* ── Divider ── */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-gray-700" />
          <span className="text-gray-500 text-xs font-['Oswald'] uppercase tracking-wider">Ny medlem?</span>
          <div className="flex-1 h-px bg-gray-700" />
        </div>

        {/* ── FIELD 2: Dagskode (new member daily code) ── */}
        <form onSubmit={handleDailySubmit} className="mc-card p-6 border-green-500/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-600/10 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-['Oswald'] font-bold text-white">DAGSKODE</h2>
              <p className="text-gray-500 text-xs">Fået en kode af en administrator?</p>
            </div>
          </div>

          <div className="mb-4">
            <input
              type="text"
              value={dailyCode}
              onChange={(e) => setDailyCode(e.target.value.toUpperCase())}
              className="mc-input w-full text-center text-lg tracking-widest font-mono py-3"
              placeholder="Indtast dagskode"
              autoComplete="off"
              maxLength={6}
              data-testid="daily-code-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !dailyCode.trim()}
            className="w-full py-3 rounded-xl font-['Oswald'] uppercase tracking-wider bg-green-600 hover:bg-green-500 text-white disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            data-testid="daily-code-submit"
          >
            {loading ? "Checker..." : <>Bliv medlem <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <p className="text-center text-gray-600 text-xs">
          Har du ikke en kode? Kontakt en administrator.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
